import { useEffect, useRef, useState } from 'react';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import SecondaryButton from '../Buttons/SecondaryButton';

interface Props {
    modelValue?: string | File | null;
    label?: string;
    buttonText: string;
    error?: string;
    onChange: (file: File) => void;
    cropInitValues?: Crop;
    rounded?: boolean;
}

const ImageUploadCrop = (props: Props) => {
    const [imagePreview, setImagePreview] = useState<string | null>(
        (props.modelValue as string) || null
    );
    const [crop, setCrop] = useState<Crop>({
        unit: 'px',
        width: 50,
        height: 50,
        x: 0,
        y: 0,
    });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(
        (props.modelValue as string) || null
    );
    const [error, setError] = useState<string | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const imageInputRef = useRef<HTMLInputElement | null>(null);
    const [cropping, setCropping] = useState(false);
    const [previousImage, setPreviousImage] = useState<string | null>(null);

    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (typeof props.modelValue === 'string' && props.modelValue) {
            setImagePreview(props.modelValue);
            setCroppedImageUrl(props.modelValue);
        }
    }, [props.modelValue]);

    useEffect(() => {
        setError(props.error ?? null);
    }, [props.error]);

    const dataURLtoFile = (dataurl: string, filename: string) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] ?? 'application/octet-stream';
        const bstr = atob(arr[1]);
        const n = bstr.length;
        const u8arr = new Uint8Array(n);

        for (let i = 0; i < n; i++) {
            u8arr[i] = bstr.charCodeAt(i);
        }

        return new File([u8arr], filename, { type: mime });
    };

    const updateImagePreview = (e: React.ChangeEvent<HTMLInputElement>) => {
        const image = e.target.files?.[0];

        if (!image) return;

        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;

            img.onload = () => {
                if (img.width < 200 || img.height < 200) {
                    setError('La imagen debe tener al menos 200x200 píxeles.');
                    return;
                }

                setPreviousImage(croppedImageUrl || imagePreview); // Store previous image state
                setImagePreview(e.target?.result as string);
                setCroppedImageUrl(null);
                setCropping(true);

                setCompletedCrop(undefined);
                setError(null);
            };
        };

        reader.readAsDataURL(image);
    };

    const cropImage = () => {
        if (!completedCrop || !canvasRef.current || !imageRef.current) {
            return;
        }

        const canvas = canvasRef.current;
        const image = imageRef.current;
        const crop = completedCrop;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        canvas.width = crop.width * scaleX;
        canvas.height = crop.height * scaleY;

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width * scaleX,
            crop.height * scaleY
        );

        const base64Image = canvas.toDataURL('image/jpeg');
        const file = dataURLtoFile(base64Image, 'cropped.jpg');
        props.onChange(file);
        setPreviousImage(base64Image); // Store cropped image as previous state
        setCroppedImageUrl(base64Image);
        setCropping(false);
    };

    const cancelCrop = () => {
        setImagePreview(previousImage); // Revert to previous image state
        setCropping(false);
        setError(null);
    };

    return (
        <div className="flex flex-col items-center gap-4">
            {/* Show error message */}
            {error && <div className="text-red-500">{error}</div>}

            {/* Show preview image (either default or cropped) when not cropping */}
            {(croppedImageUrl || imagePreview) && !cropping && (
                <img
                    src={croppedImageUrl ?? imagePreview ?? undefined}
                    alt="Preview"
                    className={`${props.rounded ? 'rounded-full' : 'rounded-lg'}`}
                    style={{ maxHeight: '200px' }}
                />
            )}

            {/* Show cropper when cropping */}
            {cropping && imagePreview && (
                <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1} // Force 1:1 aspect ratio
                    minWidth={100} // Minimum crop size in pixels
                >
                    <img
                        ref={imageRef}
                        alt="Crop me"
                        src={imagePreview}
                        style={{ maxHeight: '300px' }}
                    />
                </ReactCrop>
            )}

            {/* Always show upload button when not cropping */}
            {!cropping && (
                <SecondaryButton
                    onClick={() => imageInputRef.current?.click()}
                    type="button"
                >
                    {props.buttonText}
                </SecondaryButton>
            )}

            {/* Hidden file input */}
            <input
                type="file"
                accept="image/*"
                className="hidden"
                ref={imageInputRef}
                onChange={updateImagePreview}
            />

            {/* Hidden canvas for cropping */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Show crop confirmation buttons when cropping */}
            {cropping && (
                <div className="flex gap-4">
                    <SecondaryButton onClick={cropImage}>
                        Recortar
                    </SecondaryButton>
                    <SecondaryButton onClick={cancelCrop}>
                        Cancelar
                    </SecondaryButton>
                </div>
            )}
        </div>
    );
};

export default ImageUploadCrop;
